import { Inject } from '@odinjs/odin';
import { ButtonAPI, DateFieldAPI, FieldAPI, FormAPI, PhoneNumberAPI } from '@philips-internal/oswald-ui-components';
import { Country } from '@philips-internal/oswald-ui-constants';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnChange, OnClick, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { AbortableSubmitEvent, ChangeEvent, MountEvent, SubmitEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { LanguageService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Establishment, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';
import { ScheduleWaitingListData } from '../../../app/types';
import { FragmentNames, PseudonymSchedulingWaitingListAliases } from '../../../codegen';
import AppointmentService from '../../../services/appointment-service';
import DateFormatterUtil from '../../../utils/date-utils/date-formatter-util';
import { showToastNotificationError, showToastNotificationSuccess } from '../../../utils/toast-notification-utils';
import PseudonymAppointmentsBaseController from '../pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from '../pseudonym-appointments-model';
import { PseudonymWaitingListData } from '../pseudonym-interfaces';
import { AppointmentType, ScheduleAvailableType, ScheduleType } from '../pseudonym-types';

interface PseudonymWaitingListDataForm extends PseudonymWaitingListData {
  contactName: string;
  contactNumber: string;
  desiredDate: string;
  dsNote: string;
  establishment: Establishment;
  ieUrgent: boolean;
  nrSeqAgeintItem: string;
}

interface SpecialityProps {
  cdEspecialidade: number;
  dsEspecialidade: string;
}

interface PhysicianProps {
  cdPessoaFisica?: string;
  nmPessoaFisica?: string;
}

interface ScheduleTypeProps {
  value: string;
}

@Controller({ fragment: FragmentNames.PSEUDONYM_SCHEDULING_WAITING_LIST })
export default class PseudonymSchedulingWaitingListController extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymSchedulingWaitingListAliases.FORM_WAITING_LIST)
    formWaitingListApi!: FormAPI;

  @Inject(PseudonymSchedulingWaitingListAliases.FIELD_PROCEDURE)
    fieldProceduresApi!: FieldAPI;

  @Inject(PseudonymSchedulingWaitingListAliases.FIELD_DESIRED_DATE)
    fieldDesiredDateApi!: FieldAPI;

  @Inject(PseudonymSchedulingWaitingListAliases.DATE_FIELD_DESIRED_DATE)
    dateFieldDesiredDateApi!: DateFieldAPI;

  @Inject(PseudonymSchedulingWaitingListAliases.FIELD_AVAILABLE_PERIOD)
    fieldAvailablePeriodApi!: FieldAPI;

  @Inject(PseudonymSchedulingWaitingListAliases.DATE_FIELD_AVAILABLE_PERIOD)
    dateFieldAvailablePeriodApi!: DateFieldAPI;

  @Inject(PseudonymSchedulingWaitingListAliases.FIELD_AVAILABLE_PERIOD_READONLY)
    fieldAvailablePeriodReadonlyApi!: FieldAPI;

  @Inject(PseudonymSchedulingWaitingListAliases.FIELD_URGENCY_REASON)
    fieldUrgencyReasonApi!: FieldAPI;

  @Inject(PseudonymSchedulingWaitingListAliases.BUTTON_CONFIRM)
    buttonConfirmApi!: ButtonAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @OnMount({ target: PseudonymSchedulingWaitingListAliases.FORM_WAITING_LIST })
  handleMountFormWaitingList(): void {
    const pseudonymWaitingListData = PseudonymAppointmentModel.waitingList.pseudonymWaitingListData;
    if (!pseudonymWaitingListData) {
      return;
    }

    const availablePeriod = pseudonymWaitingListData.availablePeriod;
    let availablePeriodReadonly = '';
    if (availablePeriod && availablePeriod.length > 0) {
      this.fieldAvailablePeriodApi.setVisible(false);
      this.fieldAvailablePeriodReadonlyApi.setVisible(true);
      availablePeriodReadonly = `${DateFormatterUtil.date(availablePeriod[0])} - ${DateFormatterUtil.date(availablePeriod[1])}`;

      this.dateFieldDesiredDateApi.clearRanges();
      this.dateFieldDesiredDateApi.addRange(availablePeriod[0], availablePeriod[1]);
    }

    const establishment = foundationContext.getEstablishment() as Establishment;

    const procedure = pseudonymWaitingListData.procedureList[0];
    if (procedure.typeProcedureCode === ScheduleType.CONSULTATION) {
      this.fieldProceduresApi.setVisible(false);
    }

    const speciality = {
      cdEspecialidade: Number(procedure.specialtySequence),
      dsEspecialidade: procedure.specialtyDescription
    } as SpecialityProps;

    const physician = {
      cdPessoaFisica: procedure.physicianSequence,
      nmPessoaFisica: procedure.physicianName
    } as PhysicianProps

    const scheduleType = {
      value: procedure.typeProcedureDescription
    } as ScheduleTypeProps

    this.formWaitingListApi.setData({
      availablePeriod: availablePeriod,
      availablePeriodReadOnly: availablePeriodReadonly,
      establishment: establishment,
      medicalSpeciality: speciality,
      physician: physician,
      preocedure: procedure,
      scheduleType: scheduleType,
    });
  }

  @OnValidate({ target: PseudonymSchedulingWaitingListAliases.FORM_WAITING_LIST })
  handleValidateFormWaitingList(event: ValidateEvent): void {
    this.buttonConfirmApi.setEnabled(event.valid);
  }

  @OnMount({ target: [PseudonymSchedulingWaitingListAliases.DATE_FIELD_AVAILABLE_PERIOD, PseudonymSchedulingWaitingListAliases.DATE_FIELD_DESIRED_DATE] })
  handleMountDateFieldAvailablePeriodAndDesiredDate(event: MountEvent): void {
    const dateField = event.target as DateFieldAPI;
    const pseudonymWaitingListData = PseudonymAppointmentModel.waitingList.pseudonymWaitingListData;
    const today = moment().utc().toISOString();
    dateField.addRange(today, pseudonymWaitingListData.dependencies.calendarFinalDateInitialRange);
  }

  @OnChange({ target: PseudonymSchedulingWaitingListAliases.DATE_FIELD_AVAILABLE_PERIOD })
  handleChangeDateFieldAvailablePeriod(event: ChangeEvent<Array<string>>): void {
    const selectedPeriod = event.value;
    this.dateFieldDesiredDateApi.clearRanges();

    if (selectedPeriod.length === 0) {
      const today = moment().utc().toISOString();
      const pseudonymWaitingListData = PseudonymAppointmentModel.waitingList.pseudonymWaitingListData;
      this.dateFieldDesiredDateApi.addRange(today, pseudonymWaitingListData.dependencies.calendarFinalDateInitialRange);
      return;
    }

    const startDate = selectedPeriod[0];
    const endDate = selectedPeriod[1];
    this.dateFieldDesiredDateApi.addRange(startDate, endDate);
    const currentDesiredDate = this.fieldDesiredDateApi.getValue() as string || '';
    const currentDesiredDateAsMoment = moment(currentDesiredDate, ISO_DATE_FORMAT, true);
    const currentDesiredDateisValid = currentDesiredDateAsMoment.isValid();
    if (!currentDesiredDateisValid || !currentDesiredDateAsMoment.isBetween(startDate, endDate, undefined, '[]')) {
      this.fieldDesiredDateApi.setValue('');
      this.fieldDesiredDateApi.validate();
    }
  }

  @OnChange({ target: PseudonymSchedulingWaitingListAliases.DATE_FIELD_DESIRED_DATE })
  handleChangeDateFieldDesiredDate(event: ChangeEvent<string>): void {
    const dateSelected = event.value;
    const dateSelectedAsMoment = moment(dateSelected, ISO_DATE_FORMAT, true);
    const pseudonymWaitingListData = PseudonymAppointmentModel.waitingList.pseudonymWaitingListData;
    const dateFieldAvailablePeriodData = this.fieldAvailablePeriodApi.isVisible() ? this.dateFieldAvailablePeriodApi.getValue() : pseudonymWaitingListData.availablePeriod;
    const doesNotExistsSelectedPeriod = !dateFieldAvailablePeriodData;

    if (doesNotExistsSelectedPeriod && dateSelected && dateSelectedAsMoment.isValid()) {
      const validDateFieldRange = this.dateFieldDesiredDateApi.getRanges();
      const dateFieldRange = validDateFieldRange[0];
      if (!dateSelectedAsMoment.isBetween(dateFieldRange.startDate, dateFieldRange.endDate, 'date', '[]')) {
        this.fieldDesiredDateApi.setValue('');
        this.fieldDesiredDateApi.validate();
        return;
      }

      this.dateFieldDesiredDateApi.clearRanges();
      this.dateFieldDesiredDateApi.addRange(dateSelected, dateSelected);
      this.fieldAvailablePeriodApi.setValue([dateSelected, dateSelected]);
      this.fieldAvailablePeriodApi.validate();
    }
  }

  @OnMount({ target: PseudonymSchedulingWaitingListAliases.PHONE_NUMBER_CONTACT })
  handleMountPhoneNumberContact(event: MountEvent): void {
    const phoneNumber = event.target as PhoneNumberAPI;
    const phoneCountry = LanguageService.getCurrentLanguage().split('-')[1].toLocaleUpperCase();
    phoneNumber.setCountry(phoneCountry as Country);
  }

  @OnChange({ target: PseudonymSchedulingWaitingListAliases.FIELD_IE_URGENT })
  handleChangeFieldIeUrgent(event: ChangeEvent<Record<string, Value>>): void {
    const showUrgencyReason = event.value as unknown as boolean;
    this.fieldUrgencyReasonApi.setVisible(showUrgencyReason);

    if (showUrgencyReason) {
      this.fieldUrgencyReasonApi.addValidator({ name: 'required', options: {} });
    } else {
      this.fieldUrgencyReasonApi.removeValidator('required');
    }
    this.fieldUrgencyReasonApi.validate();
  }

  @OnMount({ target: PseudonymSchedulingWaitingListAliases.BUTTON_CONFIRM })
  handleMountConfirmButton(): void {
    this.buttonConfirmApi.setEnabled(false);
  }

  @OnClick({ target: PseudonymSchedulingWaitingListAliases.BUTTON_CONFIRM })
  handleClickConfirmButton(): void {
    this.formWaitingListApi.submit();
  }

  @OnClick({ target: PseudonymSchedulingWaitingListAliases.BUTTON_CANCEL })
  handleClickCancelButton(): void {
    this.submittableFragmentContext.cancel(new SubmitEvent());
  }

  @OnBeforeSubmit({ target: PseudonymSchedulingWaitingListAliases.FORM_WAITING_LIST })
  handleBeforeSubmitFormWaitingList(event: AbortableSubmitEvent): void {
    const record = event.record as PseudonymWaitingListDataForm;
    const scheduleWaitingListData = this.mountScheduleWaitingListData(record);

    const haveToReturnWaitingListItem = this.context.get('haveToReturnWaitingListItem') as boolean;
    if (haveToReturnWaitingListItem) {
      this.submittableFragmentContext.submit(new SubmitEvent({ record: scheduleWaitingListData }));
      return;
    }

    this.confirmAndRequestAppointments(scheduleWaitingListData);
  }

  private mountScheduleWaitingListData(waitingListDataForm: PseudonymWaitingListDataForm): ScheduleWaitingListData {
    const pseudonymWaitingListData = PseudonymAppointmentModel.waitingList.pseudonymWaitingListData;
    const dependencies = pseudonymWaitingListData.dependencies;
    const procedure = pseudonymWaitingListData.procedureList[0];
    const startAt = waitingListDataForm.availablePeriod && waitingListDataForm.availablePeriod.length >= 1 ? waitingListDataForm.availablePeriod[0] : null;
    const endAt = waitingListDataForm.availablePeriod && waitingListDataForm.availablePeriod.length >= 2 ? waitingListDataForm.availablePeriod[1] : startAt;
    let type = '';

    switch (procedure.typeProcedureCode) {
      case ScheduleType.CONSULTATION:
        type = AppointmentType.CONSULTATIONS;
        break;
      case ScheduleType.EXAM:
        type = AppointmentType.EXAMS;
        break;
      case ScheduleType.SERVICE:
        type = AppointmentType.SERVICES;
        break;
    }

    const scheduleWaitingListData = {
      availableType: ScheduleAvailableType.WAITING_LIST,
      cdCategoria: dependencies.healthInsuranceData.categoryId,
      cdConvenio: dependencies.healthInsuranceData.healthInsuranceId,
      cdEspecialidade: procedure.specialtySequence,
      cdEstabelecimento: waitingListDataForm.establishment.id,
      cdMedico: procedure.physicianSequence,
      cdPessoaFisica: dependencies.currentPatientSelected.id,
      cdPlano: dependencies.healthInsuranceData.planId,
      contactName: waitingListDataForm.contactName,
      contactNumber: waitingListDataForm.contactNumber,
      desiredDate: waitingListDataForm.desiredDate,
      dsNota: waitingListDataForm.dsNote,
      dtAgenda: waitingListDataForm.desiredDate,
      endAt: endAt,
      ieUrgent: waitingListDataForm.ieUrgent,
      nrSeqAgeint: pseudonymWaitingListData.nrSeqAgeint,
      nrSeqAgeintItem: pseudonymWaitingListData.nrSeqAgeintItem,
      nrSeqProcInterno: procedure.internalProcedureSequence,
      startAt: startAt,
      type: type.toUpperCase(),
      urgencyReasonId: waitingListDataForm.urgencyReason?.nrSequence,
    } as ScheduleWaitingListData;

    return scheduleWaitingListData;
  }

  private confirmAndRequestAppointments(scheduleWaitingListData: ScheduleWaitingListData): void {
    this.buttonConfirmApi.setEnabled(false);
    this.formWaitingListApi.setLoading(true);

    AppointmentService.confirmAndRequestAppointments(
      scheduleWaitingListData.nrSeqAgeint,
      scheduleWaitingListData.cdPessoaFisica,
      [scheduleWaitingListData] as unknown as Array<Record<string, Value>>)
      .then(() => {
        showToastNotificationSuccess(MessageService.translate('patient.schedule-success'));
        this.submittableFragmentContext.cancel(new SubmitEvent());
      }).catch(() => {
        this.buttonConfirmApi.setEnabled(true);
        this.formWaitingListApi.setEditing(true);
        showToastNotificationError(MessageService.translate('patient.appointments.confirm-failed'));
      }).finally(() => {
        this.formWaitingListApi.setLoading(false)
      });
  }

}
